import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @ts-ignore
import { getSeoByRoute } from '../services/seoService';
// @ts-ignore
import { getSeoForCustomer } from '../services/settingService';

/**
 * Hook để tự động lấy SEO từ database dựa trên route hiện tại
 * và cập nhật meta tags
 * 
 * Nếu không tìm thấy SEO cho route, sẽ fallback về SEO từ settings (cho home page)
 */
export const useAutoSEO = () => {
  const location = useLocation();

  useEffect(() => {
    const fetchAndApplySEO = async () => {
      try {
        const route = location.pathname;

        // Try to get SEO from database
        try {
          const response = await getSeoByRoute(route);
          if (response && response.success && response.data) {
            const data = response.data;
            applySEOTags({
              metaTitle: data.metaTitle,
              metaDescription: data.metaDescription,
              metaKeywords: Array.isArray(data.metaKeywords) 
                ? data.metaKeywords.join(', ') 
                : data.metaKeywords,
              ogTitle: data.ogTitle || data.metaTitle,
              ogDescription: data.ogDescription || data.metaDescription,
              ogImage: data.ogImage,
              twitterCard: data.twitterCard || 'summary_large_image',
              twitterTitle: data.twitterTitle || data.metaTitle,
              twitterDescription: data.twitterDescription || data.metaDescription,
              twitterImage: data.twitterImage,
              canonicalUrl: data.canonicalUrl,
              robots: data.robots
            });
            return;
          }
        } catch (error: any) {
          // If route not found (404), that's normal - continue to fallback
          // Only log other errors
          if (error.response?.status !== 404) {
            console.error('Error fetching SEO by route:', error);
          }
        }

        // Fallback: For home page, use settings SEO
        if (route === '/' || route === '') {
          try {
            const settingsResponse = await getSeoForCustomer();
            if (settingsResponse.success && settingsResponse.data) {
              const data = settingsResponse.data;
              applySEOTags({
                metaTitle: data.metaTitle,
                metaDescription: data.metaDescription,
                metaKeywords: data.metaKeywords,
                ogTitle: data.metaTitle,
                ogDescription: data.metaDescription,
                ogImage: undefined,
                twitterCard: 'summary_large_image',
                twitterTitle: data.metaTitle,
                twitterDescription: data.metaDescription,
                twitterImage: undefined,
                canonicalUrl: data.canonicalUrl || (typeof window !== 'undefined' ? window.location.origin : undefined),
                robots: data.robots || 'index, follow'
              });
            }
          } catch (error) {
            console.error('Error fetching settings SEO:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching SEO:', error);
      }
    };

    fetchAndApplySEO();
  }, [location.pathname]);
};

interface SEOTags {
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  robots?: string;
}

/**
 * Helper function to apply SEO tags to document
 */
const applySEOTags = (tags: SEOTags) => {
  // Update document title
  if (tags.metaTitle) {
    document.title = tags.metaTitle;
  }

  // Update meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  if (tags.metaDescription) {
    metaDescription.setAttribute('content', tags.metaDescription);
  }

  // Update meta keywords
  if (tags.metaKeywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', tags.metaKeywords);
  }

  // Update robots
  if (tags.robots) {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', tags.robots);
  }

  // Update Open Graph tags
  if (tags.ogTitle) {
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', tags.ogTitle);
  }

  if (tags.ogDescription) {
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', tags.ogDescription);
  }

  if (tags.ogImage) {
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', tags.ogImage);
  }

  // Update Open Graph URL
  let ogUrl = document.querySelector('meta[property="og:url"]');
  if (!ogUrl) {
    ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    document.head.appendChild(ogUrl);
  }
  ogUrl.setAttribute('content', tags.canonicalUrl || window.location.href);

  // Update Twitter Card tags
  if (tags.twitterCard) {
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement('meta');
      twitterCard.setAttribute('name', 'twitter:card');
      document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute('content', tags.twitterCard);
  }

  if (tags.twitterTitle) {
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitle) {
      twitterTitle = document.createElement('meta');
      twitterTitle.setAttribute('name', 'twitter:title');
      document.head.appendChild(twitterTitle);
    }
    twitterTitle.setAttribute('content', tags.twitterTitle);
  }

  if (tags.twitterDescription) {
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (!twitterDescription) {
      twitterDescription = document.createElement('meta');
      twitterDescription.setAttribute('name', 'twitter:description');
      document.head.appendChild(twitterDescription);
    }
    twitterDescription.setAttribute('content', tags.twitterDescription);
  }

  if (tags.twitterImage) {
    let twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (!twitterImage) {
      twitterImage = document.createElement('meta');
      twitterImage.setAttribute('name', 'twitter:image');
      document.head.appendChild(twitterImage);
    }
    twitterImage.setAttribute('content', tags.twitterImage);
  }

  // Update canonical URL
  if (tags.canonicalUrl) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', tags.canonicalUrl);
  }
};
